import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, MapPin, Wifi, WifiOff, Navigation, Square, Gauge, AlertTriangle, LogIn, LogOut } from 'lucide-react';
import { getNotificationPrefs } from '@/lib/notificationPrefs';

const EVENT_ICONS = {
  deviceOnline: { icon: Wifi, color: '#10b981', bg: '#ecfdf5' },
  deviceOffline: { icon: WifiOff, color: '#94a3b8', bg: '#f1f5f9' },
  deviceMoving: { icon: Navigation, color: '#3b82f6', bg: '#eff6ff' },
  deviceStopped: { icon: Square, color: '#ef4444', bg: '#fef2f2' },
  deviceOverspeed: { icon: Gauge, color: '#f59e0b', bg: '#fffbeb' },
  geofenceEnter: { icon: LogIn, color: '#8b5cf6', bg: '#f5f3ff' },
  geofenceEntry: { icon: LogIn, color: '#8b5cf6', bg: '#f5f3ff' }, // rule fires carry the condition type (TASK-338)
  geofenceExit: { icon: LogOut, color: '#f97316', bg: '#fff7ed' },
  alarm: { icon: AlertTriangle, color: '#ef4444', bg: '#fef2f2' },
  ignitionOn: { icon: Wifi, color: '#10b981', bg: '#ecfdf5' },
  ignitionOff: { icon: WifiOff, color: '#94a3b8', bg: '#f1f5f9' },
};

const DEFAULT_ICON = { icon: MapPin, color: '#3b82f6', bg: '#eff6ff' };

export default function NotificationToast() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const notifications = useSelector((state) => state.notifications.items);
  const devices = useSelector((state) => state.devices.items);
  const staffByUserId = useSelector((state) => state.staff.byUserId);
  const [visible, setVisible] = useState([]);

  const prefs = getNotificationPrefs();

  // Show new notifications as toasts
  useEffect(() => {
    if (notifications.length === 0) return;
    const latest = notifications[0];
    if (!latest || visible.some((v) => v._id === latest._id)) return;

    setVisible((prev) => {
      const next = [latest, ...prev].slice(0, prefs.maxToasts);
      return next;
    });
  }, [notifications]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-dismiss
  useEffect(() => {
    if (visible.length === 0) return;
    const timer = setTimeout(() => {
      setVisible((prev) => prev.slice(0, -1));
    }, prefs.toastDuration * 1000);
    return () => clearTimeout(timer);
  }, [visible, prefs.toastDuration]);

  const dismiss = useCallback((id) => {
    setVisible((prev) => prev.filter((v) => v._id !== id));
  }, []);

  const handleClick = useCallback((notif) => {
    dismiss(notif._id);
    navigate('/map');
  }, [dismiss, navigate]);

  if (visible.length === 0) return null;

  return (
    <div className="fixed bottom-4 z-50 flex flex-col gap-2 pointer-events-none" style={{ insetInlineEnd: '1rem' }}>
      {visible.map((notif) => {
        const evtConfig = EVENT_ICONS[notif.type] || DEFAULT_ICON;
        const Icon = evtConfig.icon;
        return (
          <div
            key={notif._id}
            className="pointer-events-auto w-80 bg-white dark:bg-slate-900 rounded-xl shadow-lg dark:shadow-black/30 border border-slate-200/80 dark:border-slate-800 p-3 flex items-start gap-3 animate-slide-in cursor-pointer hover:shadow-xl transition-shadow"
            onClick={() => handleClick(notif)}
          >
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: evtConfig.bg }}
            >
              <Icon size={16} style={{ color: evtConfig.color }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-slate-800 dark:text-slate-200">
                {t(`alerts.events.${notif.type}`, notif.type)}
              </p>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                <bdi>{notif.deviceName
                  || devices[notif.deviceId]?.name
                  || staffByUserId[notif.position?.userId]?.name
                  || notif.snapshot?.poiName
                  || '—'}</bdi>
                {notif.locationLabel ? ` · ${notif.locationLabel}` : ''}
              </p>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); dismiss(notif._id); }}
              className="p-1 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-600 dark:hover:text-slate-300 transition-all flex-shrink-0"
            >
              <X size={14} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
